import { FormProvider, useFormContext } from 'react-hook-form';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle, ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import { useMemo } from 'react';
import {
  FormTreeData,
  FormFieldValue,
  VisibilityChangeCallback,
  NavigationSection,
  SubmissionFormData,
} from './types';
import FormRenderer from './FormRenderer';
import { FormSidebar } from './components/FormSidebar';
import { FormSubmitSection } from './components/FormSubmitSection';
import {
  buildQuestionData,
  useFormTree,
} from '@/hooks/submission_forms/useFormTree';
import { usePrintMode } from '@/hooks/submission_forms/usePrintMode';
import { useDynamicFormOrchestration } from '@/hooks/submission_forms/useDynamicFormOrchestration';
import { useBranchClearGuard } from '@/hooks/submission_forms/useBranchClearGuard';
import { BranchClearConfirmDialog } from './components/BranchClearConfirmDialog';
import { useSectionClarifications } from '@/hooks/clarifications/useSectionClarifications';
import { useClarificationsContext } from '@/contexts/ClarificationsContext';
import { isClarificationsActiveStatus } from '@/utils/clarificationStatus';
import { useNavigate, useParams } from 'react-router-dom';
import { SubmissionFormProvider } from './SubmissionFormContext';
import {
  ClarificationNavigationProvider,
  useFormDisclosure,
} from './ClarificationNavigationContext';
import { ClarificationNavigator } from './components/ClarificationNavigator';

interface DynamicFormProps {
  formTree: FormTreeData[];
  savedAnswers?: Record<string, FormFieldValue>;
  readOnly?: boolean;
}

interface FormLayoutProps {
  formTree: FormTreeData[];
  isSubmitted: boolean;
  submissionFormData: SubmissionFormData | null;
  printView: boolean;
  readOnly: boolean;
  onVisibilityChange: VisibilityChangeCallback;
  onSubmit: () => Promise<void>;
  submitError: string | null;
  isSubmitting: boolean;
  hasClarifications: boolean;
  showClarificationNavigator: boolean;
  sidebarProps: {
    navigationSections: NavigationSection[];
    progressPercentage: number;
    lastSaved: string | null | undefined;
    isSaving: boolean;
    isProgressStale: boolean;
    saveError: string | null;
    isSubmitting: boolean;
  };
}

/**
 * Form layout component with access to form context
 * Handles the visual layout of sidebar, form content, and submit section
 */
const FormLayout = ({
  formTree,
  isSubmitted,
  submissionFormData,
  printView,
  readOnly,
  onVisibilityChange,
  onSubmit,
  submitError,
  isSubmitting,
  hasClarifications,
  showClarificationNavigator,
  sidebarProps,
}: FormLayoutProps) => {
  const { handleSubmit: formHandleSubmit, formState } = useFormContext();
  const disclosure = useFormDisclosure();
  const { totalClarificationCount } = useClarificationsContext();
  // Mirrors ClarificationNavigator's own guard so the padding below matches
  const navigatorVisible =
    showClarificationNavigator && !printView && totalClarificationCount > 0;
  // Only show the validation banner after the user has attempted to submit at
  // least once (submitCount > 0), so we don't pre-emptively flag empty fields.
  const hasValidationErrors =
    formState.submitCount > 0 && !formState.isValid && !isSubmitted;
  // Derive directly from submissionFormData rather than the isSubmitted prop,
  // which lags by one render (it updates via useEffect after submissionFormData
  // changes, causing a flash where the sidebar briefly appears then hides).
  const showSidebar =
    submissionFormData !== null &&
    (submissionFormData.status === 'draft' || hasClarifications) &&
    !printView;
  // Form is disabled when submitted or in read-only mode
  const isDisabled = isSubmitted || readOnly;

  return (
    <form
      onSubmit={formHandleSubmit(onSubmit)}
      className="w-full max-w-full overflow-x-hidden"
    >
      {/* Fixed Sidebar Navigation */}
      {showSidebar && <FormSidebar {...sidebarProps} />}

      {navigatorVisible && <ClarificationNavigator withSidebar={showSidebar} />}

      {/* Main Form Content — extra top padding clears the fixed navigator */}
      <div
        className={`${showSidebar ? 'lg:ml-80' : ''} min-h-screen flex justify-center ${navigatorVisible ? 'pt-44' : 'pt-32'} pb-12 px-6 max-w-full overflow-x-hidden`}
      >
        <div className="w-full max-w-4xl">
          <div className="flex items-center justify-between mb-8">
            <div className="font-semibold text-gray-900 text-3xl">
              {submissionFormData?.display_id}
            </div>
            <div className="flex print:hidden">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shadow-none bg-white! rounded-r-none"
                onClick={() => disclosure?.setAllSectionsOpen(true)}
              >
                Expand All
                <ChevronsUpDown className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shadow-none bg-white! rounded-l-none -ml-px"
                onClick={() => disclosure?.setAllSectionsOpen(false)}
              >
                Collapse All
                <ChevronsDownUp className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <FormRenderer
            formTree={formTree}
            disabled={isDisabled}
            onVisibilityChange={onVisibilityChange}
          />

          {/* Only show submit section when not in read-only mode */}
          {!readOnly && (
            <FormSubmitSection
              isSubmitted={isSubmitted}
              isSubmitting={isSubmitting}
              submitError={submitError}
              printView={printView}
              hasValidationErrors={hasValidationErrors}
            />
          )}
        </div>
      </div>
    </form>
  );
};

/**
 * Main DynamicForm component
 * A thin wrapper that coordinates form building and rendering
 */
export const DynamicForm = ({
  formTree,
  savedAnswers,
  readOnly = false,
}: DynamicFormProps) => {
  const { submissionId } = useParams<{ submissionId?: string }>();
  const navigate = useNavigate();

  // Print mode (needed for form tree building)
  const { printTemplate } = usePrintMode();

  // Build form schema and navigation from tree
  const { form, navigationSections } = useFormTree(
    formTree,
    printTemplate ? {} : savedAnswers
  );

  // Orchestrate all submission-related state and logic first (to get submission status)
  const {
    printView,
    isSubmitted,
    isSubmitting,
    isSaving,
    lastSaved,
    isProgressStale,
    saveError,
    submitError,
    percentComplete,
    visibleNavigationSections: initialVisibleNavigationSections,
    handleSubmit,
    handleVisibilityChange,
    submissionFormData,
    refreshSubmissionForm,
  } = useDynamicFormOrchestration({
    form,
    formTree,
    navigationSections,
    savedAnswers,
    onSuccess: () =>
      setTimeout(() => {
        navigate(
          submissionId ? `/submissions/${submissionId}` : '/submissions'
        );
        window.scrollTo(0, 0);
      }, 2000),
  });

  // Slug → question map so fields/status badges can resolve visibility
  // through the whole depends_on chain
  const questionsBySlug = useMemo(
    () => new Map(buildQuestionData(formTree).map(q => [q.slug, q])),
    [formTree]
  );

  // Warn before a gating-answer change wipes previously-answered branch questions
  const {
    pending: pendingBranchClear,
    confirm: confirmBranchClear,
    cancel: cancelBranchClear,
  } = useBranchClearGuard({
    form,
    formTree,
    enabled: !readOnly && !printTemplate && !isSubmitted,
  });

  // Compute section-level clarification info from shared context
  // No API calls here - data comes from ClarificationsProvider
  const { sectionClarifications } = useSectionClarifications(formTree);

  // Backend permission flags are the single source of truth for clarification
  // capabilities. No submission_role inspection needed.
  const { canAddClarifications, canResolveClarifications, canReplyOnly } =
    useClarificationsContext();

  // Show section-level clarification indicators when the user has any
  // clarification capability and the submission is in an active status.
  const shouldShowClarificationInfo = useMemo(() => {
    if (!submissionFormData) return false;
    if (!isClarificationsActiveStatus(submissionFormData.status)) return false;

    return canAddClarifications || canResolveClarifications || canReplyOnly;
  }, [
    submissionFormData,
    canAddClarifications,
    canResolveClarifications,
    canReplyOnly,
  ]);

  // Merge clarification info into visible navigation sections (memoized to prevent infinite loops)
  const visibleNavigationSections = useMemo(() => {
    return initialVisibleNavigationSections.map(section => {
      if (!shouldShowClarificationInfo) {
        return section;
      }
      const clarificationInfo = sectionClarifications.get(section.id);
      return {
        ...section,
        hasClarifications: clarificationInfo?.hasClarifications || false,
        unresolvedClarificationCount: clarificationInfo?.unresolvedCount || 0,
        totalClarificationCount: clarificationInfo?.totalCount || 0,
      };
    });
  }, [
    initialVisibleNavigationSections,
    sectionClarifications,
    shouldShowClarificationInfo,
  ]);

  // Check if the clarification workflow should keep the sidebar visible.
  // Admins need the sidebar on submitted forms even before any clarifications
  // exist so they can navigate sections and add new ones.
  const hasClarifications = useMemo(() => {
    if (!shouldShowClarificationInfo) return false;
    if (canAddClarifications) return true;
    return (
      Array.from(sectionClarifications.values()).some(
        info => info.hasClarifications
      ) || false
    );
  }, [
    sectionClarifications,
    shouldShowClarificationInfo,
    canAddClarifications,
  ]);

  // Handle empty form tree
  if (formTree.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          No questions available for this form.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen overflow-x-hidden">
      <FormProvider {...form}>
        <SubmissionFormProvider
          submissionFormData={submissionFormData}
          submissionId={submissionId}
          refreshSubmissionForm={refreshSubmissionForm}
          questionsBySlug={questionsBySlug}
        >
          <ClarificationNavigationProvider
            formTree={formTree}
            questionsBySlug={questionsBySlug}
            enabled={shouldShowClarificationInfo}
          >
            <FormLayout
              formTree={formTree}
              isSubmitted={isSubmitted}
              submissionFormData={submissionFormData}
              printView={printView}
              readOnly={readOnly}
              onVisibilityChange={handleVisibilityChange}
              onSubmit={handleSubmit}
              submitError={submitError}
              isSubmitting={isSubmitting}
              hasClarifications={hasClarifications}
              showClarificationNavigator={shouldShowClarificationInfo}
              sidebarProps={{
                navigationSections: visibleNavigationSections,
                progressPercentage: percentComplete,
                lastSaved,
                isSaving,
                isProgressStale,
                saveError,
                isSubmitting,
              }}
            />
          </ClarificationNavigationProvider>
        </SubmissionFormProvider>
        <BranchClearConfirmDialog
          pending={pendingBranchClear}
          onConfirm={confirmBranchClear}
          onCancel={cancelBranchClear}
        />
      </FormProvider>
    </div>
  );
};
